import calculatePayment, { affordability, LOAN_TYPE } from '@zillow/mortgage-calculations';
import { SELF_REPORTED_CREDIT_OPTIONS_VALUES } from '../../constants';
import { OUTCOME } from './constants';

const { affordabilityByDTI } = affordability;

const LTV = 95;
const DTI = 36;
const MIN_LOAN_AMOUNT = 50_000;
const MIN_CREDIT_SCORE = 620;
const MONTHLY_HOME_INSURANCE_CONSTANT = 0.00035;

export const isHappyOutcome = outcome => [OUTCOME.ZHL_PREQUAL, OUTCOME.ZGMI_CQ].includes(outcome);

export const determineOutcome = ({ loanAmount, creditScore, state }) => {
    const creditScoreValues = SELF_REPORTED_CREDIT_OPTIONS_VALUES[creditScore];

    if (creditScoreValues.creditScoreHigh < MIN_CREDIT_SCORE) {
        return OUTCOME.LOW_CREDIT;
    } else {
        if (loanAmount >= MIN_LOAN_AMOUNT) {
            return state?.toUpperCase() === 'NY' ? OUTCOME.ZGMI_CQ : OUTCOME.ZHL_PREQUAL;
        }

        if (loanAmount < MIN_LOAN_AMOUNT) {
            return OUTCOME.HIGH_DEBT;
        }
    }
};

export const calculateHomePriceBasedOnDownPayment = downPayment => downPayment / (1 - LTV / 100);
const calculateMonthlyHomeownersInsurance = downPayment => {
    const assumedHomePrice = calculateHomePriceBasedOnDownPayment(downPayment);
    return assumedHomePrice * MONTHLY_HOME_INSURANCE_CONSTANT;
};

export const calculateAffforability = ({
    annualIncome,
    monthlyDebts,
    downPayment,
    mortgageRate,
    propertyTaxRate,
    creditScore,
    state,
}) => {
    const monthlyHomeownersInsurance = calculateMonthlyHomeownersInsurance(downPayment);
    const inputs = {
        dti: DTI,
        annualIncome,
        monthlyDebts,
        downPayment,
        rate: mortgageRate,
        term: 360, // 30 years fixed
        loanType: LOAN_TYPE.FIXED,
        propertyTaxRate,
        includePropertyTax: true,
        annualHomeownersInsurance: monthlyHomeownersInsurance * 12,
        includeHomeownersInsurance: true,
        includePMI: true,
        includeTaxesInsurance: true,
    };

    const { affordabilityByDTI: homePrice, ltv } = affordabilityByDTI(inputs);
    const loanAmount = homePrice - downPayment;
    const paymentOutputs = calculatePayment({
        ...inputs,
        homePrice,
    });
    const outcome = determineOutcome({ loanAmount, creditScore, state });

    return {
        homePrice: Math.floor(homePrice),
        ltv,
        loanAmount,
        lowerBoundHomePrice: homePrice * 0.95,
        upperBoundHomePrice: homePrice * 1.05,
        monthlyPayment: paymentOutputs?.monthlyPayment,
        monthlyPI: paymentOutputs?.monthlyPI,
        monthlyPropertyTax: paymentOutputs?.monthlyPropertyTax,
        monthlyHomeownersInsurance,
        monthlyPMI: paymentOutputs?.monthlyPMI,
        outcome,
        mortgageRate,
        propertyTaxRate,
    };
};
